import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Activity, CheckCircle, XCircle } from 'lucide-react';
import { AdminNavigation } from '../../components/Beehive/AdminNavigation.js';

interface TwoFactorStatus {
  userId: string;
  username: string;
  enabled: boolean;
  lastLogin: string;
  setupDate?: string;
  backupCodes: number;
}

interface QuarantineEntry {
  id: string;
  fileName: string;
  uploadTime: string;
  threat: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  userId: string;
  action: 'quarantined' | 'deleted' | 'cleaned';
  scanEngine: string;
}

interface SecurityMetric {
  name: string;
  value: number;
  unit: string;
  trend: 'up' | 'down' | 'neutral';
  severity: 'info' | 'warning' | 'error';
  description: string;
}

const SecurityPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'2fa' | 'quarantine' | 'metrics'>('2fa');
  const [twoFactorData, setTwoFactorData] = useState<TwoFactorStatus[]>([]);
  const [quarantineData, setQuarantineData] = useState<QuarantineEntry[]>([]);
  const [securityMetrics, setSecurityMetrics] = useState<SecurityMetric[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSecurityData = async () => {
      setLoading(true);
      try {
        const [twoFactorResponse, quarantineResponse, metricsResponse] = await Promise.all([
          fetch('/api/admin/2fa-status'),
          fetch('/api/admin/quarantine-log'),
          fetch('/api/admin/metrics?type=security')
        ]);

        if (twoFactorResponse.ok) {
          const twoFactorResult = await twoFactorResponse.json();
          setTwoFactorData(twoFactorResult);
        } else {
          setTwoFactorData([
            {
              userId: 'user_001',
              username: 'admin@beehive.com',
              enabled: true,
              lastLogin: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
              setupDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
              backupCodes: 8
            },
            {
              userId: 'user_002',
              username: 'developer@beehive.com',
              enabled: false,
              lastLogin: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
              backupCodes: 0
            },
            {
              userId: 'user_003',
              username: 'analyst@beehive.com',
              enabled: true,
              lastLogin: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
              setupDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
              backupCodes: 10
            }
          ]);
        }

        if (quarantineResponse.ok) {
          const quarantineResult = await quarantineResponse.json();
          setQuarantineData(quarantineResult);
        } else {
          setQuarantineData([
            {
              id: 'quar_001',
              fileName: 'suspicious_script.js',
              uploadTime: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
              threat: 'Potential malware detected',
              severity: 'high',
              userId: 'user_004',
              action: 'quarantined',
              scanEngine: 'ClamAV'
            },
            {
              id: 'quar_002',
              fileName: 'infected_document.pdf',
              uploadTime: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
              threat: 'Trojan.Generic.KD.12345',
              severity: 'critical',
              userId: 'user_005',
              action: 'deleted',
              scanEngine: 'ClamAV'
            },
            {
              id: 'quar_003',
              fileName: 'phishing_email.eml',
              uploadTime: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
              threat: 'Phishing attempt detected',
              severity: 'medium',
              userId: 'user_006',
              action: 'cleaned',
              scanEngine: 'ClamAV'
            }
          ]);
        }

        if (metricsResponse.ok) {
          const metricsResult = await metricsResponse.json();
          setSecurityMetrics(metricsResult);
        } else {
          setSecurityMetrics([
            {
              name: 'Failed Login Attempts',
              value: 23,
              unit: 'attempts/24h',
              trend: 'up',
              severity: 'warning',
              description: 'Number of failed authentication attempts in the last 24 hours'
            },
            {
              name: 'Active 2FA Users',
              value: 85,
              unit: '%',
              trend: 'up',
              severity: 'info',
              description: 'Percentage of users with two-factor authentication enabled'
            },
            {
              name: 'Quarantined Files',
              value: 12,
              unit: 'files',
              trend: 'down',
              severity: 'info',
              description: 'Number of files currently in quarantine'
            },
            {
              name: 'Security Alerts',
              value: 3,
              unit: 'alerts',
              trend: 'neutral',
              severity: 'error',
              description: 'Active security alerts requiring attention'
            },
            {
              name: 'Scan Coverage',
              value: 98.5,
              unit: '%',
              trend: 'up',
              severity: 'info',
              description: 'Percentage of uploaded files successfully scanned'
            },
            {
              name: 'Average Response Time',
              value: 1.2,
              unit: 'seconds',
              trend: 'down',
              severity: 'info',
              description: 'Average time to detect and respond to security threats'
            }
          ]);
        }
      } catch (error) {
        console.error('Failed to fetch security data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSecurityData();
    const interval = setInterval(fetchSecurityData, 60000);
    return () => clearInterval(interval);
  }, []);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-400 bg-red-500/20';
      case 'high': return 'text-orange-400 bg-orange-500/20';
      case 'medium': return 'text-yellow-400 bg-yellow-500/20';
      case 'low': return 'text-green-400 bg-green-500/20';
      case 'info': return 'text-blue-400 bg-blue-500/20';
      case 'warning': return 'text-yellow-400 bg-yellow-500/20';
      case 'error': return 'text-red-400 bg-red-500/20';
      default: return 'text-slate-400 bg-slate-500/20';
    }
  };

  const MetricCard: React.FC<{
    metric: SecurityMetric;
  }> = ({ metric }) => {
    return (
      <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <Activity className="w-5 h-5 text-slate-300" />
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(metric.severity)}`}>
            {metric.severity}
          </span>
        </div>
        <div className="text-2xl font-bold text-white mb-1">
          {metric.value} {metric.unit}
        </div>
        <div className="text-sm text-slate-400 mb-2">{metric.name}</div>
        <div className="text-xs text-slate-500">{metric.description}</div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div data-devinid="dashboard-security-panel" className="flex flex-col h-full p-6 space-y-6">
      <AdminNavigation />
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-500/20 rounded-lg">
            <Shield className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Security Dashboard</h1>
            <p className="text-slate-400">Monitor authentication, threats, and security metrics</p>
          </div>
        </div>
      </div>

      <div className="flex gap-4 border-b border-slate-700/50">
        <button
          onClick={() => setActiveTab('2fa')}
          className={`pb-3 px-1 border-b-2 transition-colors ${
            activeTab === '2fa'
              ? 'border-blue-400 text-blue-400'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
        >
          2FA Status
        </button>
        <button
          onClick={() => setActiveTab('quarantine')}
          className={`pb-3 px-1 border-b-2 transition-colors ${
            activeTab === 'quarantine'
              ? 'border-blue-400 text-blue-400'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
        >
          Quarantine Log
        </button>
        <button
          onClick={() => setActiveTab('metrics')}
          className={`pb-3 px-1 border-b-2 transition-colors ${
            activeTab === 'metrics'
              ? 'border-blue-400 text-blue-400'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
        >
          Alert Metrics
        </button>
      </div>

      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1"
      >
        {activeTab === '2fa' && (
          <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-lg overflow-hidden">
            <div className="p-4 glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border-b border-slate-700/50">
              <h3 className="text-lg font-semibold text-white">Two-Factor Authentication Status</h3>
              <p className="text-sm text-slate-400">Monitor 2FA adoption and user security status</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border-b border-slate-700/50">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">User</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">2FA Status</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">Setup Date</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">Last Login</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">Backup Codes</th>
                  </tr>
                </thead>
                <tbody>
                  {twoFactorData.map((user, index) => (
                    <motion.tr
                      key={user.userId}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="border-b border-slate-700/30 hover:bg-slate-800/30"
                    >
                      <td className="p-4 text-slate-300">{user.username}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {user.enabled ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-400" />
                          )}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.enabled ? 'text-green-400 bg-green-500/20' : 'text-red-400 bg-red-500/20'
                          }`}>
                            {user.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </td>
                      <td className="p-4 text-slate-300 text-sm">
                        {user.setupDate ? new Date(user.setupDate).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="p-4 text-slate-300 text-sm">
                        {new Date(user.lastLogin).toLocaleString()}
                      </td>
                      <td className="p-4 text-slate-300 text-sm">
                        {user.backupCodes} remaining
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'quarantine' && (
          <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-lg overflow-hidden">
            <div className="p-4 glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border-b border-slate-700/50">
              <h3 className="text-lg font-semibold text-white">Quarantine Log</h3>
              <p className="text-sm text-slate-400">Files detected and quarantined by security scanning</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border-b border-slate-700/50">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">File Name</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">Threat</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">Severity</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">Upload Time</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">User</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">Action</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-300">Scanner</th>
                  </tr>
                </thead>
                <tbody>
                  {quarantineData.map((entry, index) => (
                    <motion.tr
                      key={entry.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="border-b border-slate-700/30 hover:bg-slate-800/30"
                    >
                      <td className="p-4 font-mono text-sm text-slate-300">{entry.fileName}</td>
                      <td className="p-4 text-slate-300 text-sm max-w-xs truncate" title={entry.threat}>
                        {entry.threat}
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(entry.severity)}`}>
                          {entry.severity}
                        </span>
                      </td>
                      <td className="p-4 text-slate-300 text-sm">
                        {new Date(entry.uploadTime).toLocaleString()}
                      </td>
                      <td className="p-4 text-slate-300 text-sm">{entry.userId}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          entry.action === 'quarantined' ? 'text-yellow-400 bg-yellow-500/20' :
                          entry.action === 'deleted' ? 'text-red-400 bg-red-500/20' :
                          'text-green-400 bg-green-500/20'
                        }`}>
                          {entry.action}
                        </span>
                      </td>
                      <td className="p-4 text-slate-300 text-sm">{entry.scanEngine}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'metrics' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Security Alert Metrics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {securityMetrics.map((metric, index) => (
                  <motion.div
                    key={metric.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <MetricCard metric={metric} />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default SecurityPanel;
